import { HubConnection } from '@microsoft/signalr';
import { IAppContext } from 'App/AppContext';
import { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { getErrorMessageText, saveErrorToApi } from 'components/Notification/ErrorMessageService';
import { CopilotAction } from 'models/copilot/copilotAction';
import { getRunningProcessId, resolveProcessDefinition } from './copilotPanelContextDefinitions';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotConversation,
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import { CopilotActiveField } from 'models/copilot/copilotField';
import {
  CopilotPanelContextDefinition,
  CopilotPanelDefinitionMap,
  CopilotPanelRuntime,
} from 'models/copilot/copilotPanelContextDefinition';
import { copilotPanelResultBus } from 'models/copilot/copilotPanelResultBus';
import ProcessException, { ProcessExceptionType } from 'models/copilot/processException';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import ResourceList from 'models/resourceList';
import { Dispatch, MutableRefObject, SetStateAction, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotSubmitTaskApproval } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import { IHubConnection, stopHubConnection } from 'services/SignalR/GeneralHub';
import AppError from 'utils/appError';
import { isSkillControlRefusal, questionsCardAction } from '../../AutomationDrafting/automationDraftingAction';
import {
  getQuestionSetAnswerText,
  getQuestionSetId,
  isQuestionSetCardSubmit,
  withQuestionSetAnswers,
} from '../../Questions/questionSetCard';
import { handleFieldProposalSubmit } from '../../TextActions/fieldProposalCard';
import { CopilotApprovalOperation } from '../Dialogs/CopilotApprovalDetailsModal';
import { PendingDeleteApproval } from '../Dialogs/CopilotPanelDialogs';
import { formatApprovalDetailsValue, getApprovalOperationOutcome } from '../Dialogs/copilotApprovalHelpers';
import { mergeAdaptiveInputs } from './copilotConversationHelpers';
import { CopilotStreamAction } from './copilotStreamReducer';

interface IUseCopilotChatControllerProps {
  appContext: IAppContext;
  conversation: CopilotConversation | undefined;
  actions: CopilotAction[];
  copilotSessionId: number | undefined;
  editPreviewItems: boolean;
  hasRunningProcess: boolean;
  activeDefinition: CopilotPanelContextDefinition | undefined;
  genericDefinition: CopilotPanelContextDefinition | undefined;
  contextDefinitions: CopilotPanelDefinitionMap;
  // When true, getCopilotHubConnection is expected to return a persistent connection owned by the caller, so a
  // run must not stop it on completion. Used by the document edit modal, where the live editor stays subscribed
  // to one connection across runs (stopping it per run would drop the agent->editor command bridge). The side
  // panel leaves this false: it opens and stops a fresh connection per run.
  keepConnectionAlive?: boolean;
  abortControllerRef: MutableRefObject<AbortController | undefined>;
  getRuntime: (
    conversation?: CopilotConversation,
    actions?: CopilotAction[],
    connectionId?: string | null,
    accessToken?: string,
    targetFolder?: ResourceList,
    abortSignal?: AbortSignal,
  ) => CopilotPanelRuntime;
  getCopilotHubConnection: () => Promise<IHubConnection | null>;
  setConversation: Dispatch<SetStateAction<CopilotConversation | undefined>>;
  setCopilotSessionId: Dispatch<SetStateAction<number | undefined>>;
  setHubConnection: Dispatch<SetStateAction<HubConnection | undefined>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
  setStreamStatus: Dispatch<SetStateAction<string | undefined>>;
  setPendingDeleteApproval: Dispatch<SetStateAction<PendingDeleteApproval | null>>;
  setApprovalDetailsOperations: Dispatch<SetStateAction<CopilotApprovalOperation[] | null>>;
  dispatchStream: Dispatch<CopilotStreamAction>;
  clearPreviewState: () => void;
  cleanUpProcess: () => void;
}

// A field action's own session: the turn posts without the panel's session and document, under this title.
export type CopilotNewSession = { title: string };

const isAbortError = (err: unknown): boolean =>
  err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError');

export const useCopilotChatController = ({
  appContext,
  conversation,
  actions,
  copilotSessionId,
  editPreviewItems,
  hasRunningProcess,
  activeDefinition,
  genericDefinition,
  contextDefinitions,
  keepConnectionAlive = false,
  abortControllerRef,
  getRuntime,
  getCopilotHubConnection,
  setConversation,
  setCopilotSessionId,
  setHubConnection,
  setIsLoading,
  setIsStreaming,
  setStreamStatus,
  setPendingDeleteApproval,
  setApprovalDetailsOperations,
  dispatchStream,
  clearPreviewState,
  cleanUpProcess,
}: IUseCopilotChatControllerProps) => {
  const { t } = useTranslation(['translation', 'copilot', 'exceptions']);

  const isCancelledQuestionItem = useCallback(
    (item: CopilotConversationItem | undefined): boolean =>
      item?.type === CopilotConversionItemType.Copilot && item.response === t('copilot:Chat.QuestionCancelled'),
    [t],
  );

  const onErrorResponse = useCallback(
    (err: unknown) => {
      actions.forEach((action) => {
        action.status = TenantProcessStatus.Failed;
      });

      if (
        err instanceof AppError &&
        err.data?.code &&
        Object.values(ProcessExceptionType).includes(err.data.code as ProcessExceptionType)
      ) {
        const messageItem = ProcessException.getMessage(
          err.data.code as ProcessExceptionType,
          t,
          appContext,
          err.data?.message,
        );
        // The SharePoint-integrations card completes the setup in place (toggles, report output location)
        // and restarts the refused action once everything is there, instead of leaving a dead end. It acts
        // only after the user changed something in the card, never on mount (see actionOnMount).
        if (messageItem.type === CopilotConversionItemType.SharepointIntegrations) {
          messageItem.action = () => getRuntime().startActiveProcess();
        }
        const newConversation = conversation ? conversation.clone() : new CopilotConversation();
        newConversation.thread.push(messageItem);
        setConversation(newConversation);
      } else if (err instanceof AppError && isSkillControlRefusal(err.data?.code)) {
        // SkillControl cannot be used yet: a card that says what to do (the sign-in, or ask an administrator)
        const messageItem = new CopilotConversationItem();
        messageItem.type = CopilotConversionItemType.SkillControlAccess;
        messageItem.response = getErrorMessageText(err, t);
        messageItem.skillControlRefusal = err.data?.code;
        // the sign-in on the card restarts the refused process (the drafting process); never on mount
        messageItem.action = () => getRuntime().startActiveProcess();
        const newConversation = conversation ? conversation.clone() : new CopilotConversation();
        newConversation.thread.push(messageItem);
        setConversation(newConversation);
      } else if (err instanceof AppError) {
        const messageItem = new CopilotConversationItem();
        messageItem.type = CopilotConversionItemType.Copilot;
        messageItem.response = getErrorMessageText(err, t);
        const newConversation = conversation ? conversation.clone() : new CopilotConversation();
        newConversation.thread.push(messageItem);
        setConversation(newConversation);
        void saveErrorToApi(err, appContext, t);
      } else {
        appContext.setError(err);
      }

      clearPreviewState();
      cleanUpProcess();
    },
    [actions, appContext, cleanUpProcess, clearPreviewState, conversation, getRuntime, setConversation, t],
  );

  // the usage that runs the process: the active one, or the Generic one for a process it runs from any scene
  const getProcessDefinition = useCallback(
    (processId: TenantProcess | undefined) =>
      resolveProcessDefinition(contextDefinitions, activeDefinition, processId, getRuntime().content),
    [activeDefinition, contextDefinitions, getRuntime],
  );

  const getAnswer = useCallback(
    async (
      nextConversation: CopilotConversation,
      runtimeActions: CopilotAction[],
      targetFolder?: ResourceList,
      useGenericChatRoute: boolean = false,
      // the field a text action asked about, with the text the editor holds; travels with this one request
      activeField?: CopilotActiveField,
      // the skill the Prompt binding names; travels with this one request
      skill?: string,
      // the setId of the question card the request answers; travels with this one request
      answeredQuestionSetId?: string,
      // whether a binding's turn takes the Library's selected row; travels with this one request
      usesSelectedDocument?: boolean,
      // a field action's new session; travels with this one request
      newSession?: CopilotNewSession,
    ) => {
      setIsStreaming(true);
      let connection: IHubConnection | null = null;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        dispatchStream({ type: 'reset' });
        connection = await getCopilotHubConnection();
        const connectionId = connection?.connectionId ?? null;
        if (connectionId) {
          setHubConnection(connection?.connection);
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const answerDefinition = useGenericChatRoute
          ? contextDefinitions.get(CopilotPanelContext.Generic) ?? activeDefinition
          : getProcessDefinition(getRunningProcessId(runtimeActions));
        const runtime = getRuntime(
          nextConversation,
          runtimeActions,
          connectionId,
          accessToken,
          targetFolder,
          abortController.signal,
        );
        runtime.activeField = activeField;
        runtime.skill = skill;
        runtime.usesSelectedDocument = usesSelectedDocument;
        runtime.answeredQuestionSetId = answeredQuestionSetId;
        if (newSession) {
          // the reset of the new session lands in a later render; the runtime was built from this one
          runtime.copilotSessionId = undefined;
          runtime.selectedDocument = undefined;
          runtime.sessionTitle = newSession.title;
        }
        await answerDefinition?.getAnswer(runtime, t);
      } catch (err) {
        if (isAbortError(err)) {
          const cancelledItem = new CopilotConversationItem();
          cancelledItem.type = CopilotConversionItemType.Copilot;
          cancelledItem.response = t('copilot:Chat.QuestionCancelled');
          const newConversation = nextConversation.clone();
          newConversation.thread.push(cancelledItem);
          setConversation(newConversation);

          return;
        }

        onErrorResponse(err);
      } finally {
        setStreamStatus(undefined);
        setIsLoading(false);
        setIsStreaming(false);
        // When the caller owns a persistent connection (e.g. the document edit modal keeping the editor
        // command bridge alive across runs), leave it open; the caller stops it when its session ends.
        if (!keepConnectionAlive) await stopHubConnection(connection?.connection);
      }
    },
    [
      getProcessDefinition,
      abortControllerRef,
      activeDefinition,
      appContext,
      contextDefinitions,
      dispatchStream,
      getCopilotHubConnection,
      getRuntime,
      keepConnectionAlive,
      onErrorResponse,
      setConversation,
      setHubConnection,
      setIsLoading,
      setIsStreaming,
      setStreamStatus,
      t,
    ],
  );

  // the skill of the last turn a binding started: a question card that turn's answer asks is answered on it
  const lastSkillRef = useRef<string | undefined>(undefined);

  // a question on top of the given conversation (the current one, or a copy that already holds an answered card)
  const submitQuestion = useCallback(
    async (
      baseConversation: CopilotConversation | undefined,
      question: string,
      activeField?: CopilotActiveField,
      skill?: string,
      answeredQuestionSetId?: string,
      usesSelectedDocument?: boolean,
      newSession?: CopilotNewSession,
    ) => {
      try {
        if (editPreviewItems) {
          appContext.showNotification(t('copilot:Validation.SaveOrDiscardPreviewBeforeChat'), true);

          return;
        }

        // a new session starts empty and is a general chat, whatever process the previous render had selected
        const newConversation = baseConversation && !newSession ? baseConversation.clone() : new CopilotConversation();
        const useGenericChatRoute = newSession !== undefined || !hasRunningProcess;
        const questionDefinition = useGenericChatRoute
          ? genericDefinition
          : getProcessDefinition(getRunningProcessId(actions));
        const preparedQuestion =
          questionDefinition?.prepareQuestion(question, getRuntime(newConversation, actions), t) ?? question;
        const newRequest = newConversation.setRequestFromQuery(preparedQuestion);
        newConversation.thread.push(newRequest);
        setConversation(newConversation);
        await getAnswer(
          newConversation,
          actions,
          undefined,
          useGenericChatRoute,
          activeField,
          skill,
          answeredQuestionSetId,
          usesSelectedDocument,
          newSession,
        );
      } catch (err) {
        appContext.setError(err);
      }
    },
    [
      getProcessDefinition,
      actions,
      appContext,
      editPreviewItems,
      genericDefinition,
      getAnswer,
      getRuntime,
      hasRunningProcess,
      setConversation,
      t,
    ],
  );

  const onNewQuestion = useCallback(
    async (
      question: string,
      activeField?: CopilotActiveField,
      skill?: string,
      usesSelectedDocument?: boolean,
      newSession?: CopilotNewSession,
    ) => {
      lastSkillRef.current = skill;
      await submitQuestion(conversation, question, activeField, skill, undefined, usesSelectedDocument, newSession);
    },
    [conversation, submitQuestion],
  );

  const onEditLastQuestion = useCallback(
    async (itemId: string | undefined, question: string) => {
      try {
        if (editPreviewItems) {
          appContext.showNotification(t('copilot:Validation.SaveOrDiscardPreviewBeforeChat'), true);

          return;
        }
        if (!conversation || !itemId) return;

        const trimmedQuestion = question.trim();
        if (!trimmedQuestion) return;
        // an edited question is typed: a question card it provokes runs without the earlier turn's skill
        lastSkillRef.current = undefined;

        const newConversation = conversation.clone();
        const lastIndex = newConversation.thread.length - 1;
        const lastItem = newConversation.thread[lastIndex];
        const hasTrailingCancellation = isCancelledQuestionItem(lastItem);
        const editableIndex = hasTrailingCancellation ? lastIndex - 1 : lastIndex;
        const editableItem = newConversation.thread[editableIndex];
        if (
          editableItem?.id !== itemId ||
          editableItem.type !== CopilotConversionItemType.User ||
          !editableItem.question
        ) {
          return;
        }

        if (hasTrailingCancellation) {
          newConversation.thread.pop();
        }

        const questionDefinition = hasRunningProcess
          ? getProcessDefinition(getRunningProcessId(actions))
          : genericDefinition;
        const preparedQuestion =
          questionDefinition?.prepareQuestion(trimmedQuestion, getRuntime(newConversation, actions), t) ??
          trimmedQuestion;
        const newRequest = newConversation.setRequestFromQuery(preparedQuestion);
        newConversation.thread.splice(editableIndex, 1, newRequest);
        setConversation(newConversation);
        await getAnswer(newConversation, actions, undefined, !hasRunningProcess);
      } catch (err) {
        appContext.setError(err);
      }
    },
    [
      getProcessDefinition,
      actions,
      appContext,
      conversation,
      editPreviewItems,
      genericDefinition,
      getAnswer,
      getRuntime,
      hasRunningProcess,
      isCancelledQuestionItem,
      setConversation,
      t,
    ],
  );

  // A selection turn from inside a conversation continues it; one a scene starts through the bridge
  // opens a new one, the way a first question does. `question` is the wording a scene hands over
  // for the bubble; without it the usage words the selection itself.
  const submitEntitySelection = useCallback(
    async (
      selectedEntityIds: number[],
      question: string | undefined,
      useGenericChatRoute: boolean,
      skill?: string,
      usesSelectedDocument?: boolean,
    ) => {
      const newConversation = conversation ? conversation.clone() : new CopilotConversation();

      try {
        const selectionQuestion =
          question ?? activeDefinition?.getEntitySelectionQuestion(selectedEntityIds, t, getRuntime().content);
        const newRequest = newConversation.setRequestFromEntitySelection(selectedEntityIds, selectionQuestion);
        newConversation.thread.push(newRequest);
        setConversation(newConversation);
        await getAnswer(
          newConversation,
          actions,
          undefined,
          useGenericChatRoute,
          undefined,
          skill,
          undefined,
          usesSelectedDocument,
        );
      } catch (err) {
        onErrorResponse(err);
      }
    },
    [actions, activeDefinition, conversation, getAnswer, getRuntime, onErrorResponse, setConversation, t],
  );

  // a picker card of the running usage: its definition answers the selection
  const onEntitySelection = useCallback(
    (selectedEntityIds: number[], question?: string) => submitEntitySelection(selectedEntityIds, question, false),
    [submitEntitySelection],
  );

  // a document attached to a conversation that already asked: a fixed line is the next turn, on the skill of the
  // turn that asked, with the attached document (the preview's selected document) as the selected document
  const onDocumentAttached = useCallback(
    () =>
      submitQuestion(
        conversation,
        t('copilot:Chat.Document.AttachedContinue'),
        undefined,
        lastSkillRef.current,
        undefined,
        true,
      ),
    [conversation, submitQuestion, t],
  );

  // rows a scene selected for a prompt: answered like a typed question, generically unless a process runs
  const onSceneEntitySelection = useCallback(
    (selectedEntityIds: number[], question?: string, skill?: string, usesSelectedDocument?: boolean) => {
      lastSkillRef.current = skill;

      return submitEntitySelection(selectedEntityIds, question, !hasRunningProcess, skill, usesSelectedDocument);
    },
    [hasRunningProcess, submitEntitySelection],
  );

  const onAdaptiveInputsChanged = useCallback(
    (itemId: string | undefined, blockId: string | undefined, inputs: Record<string, string>) => {
      if (!itemId || !blockId) return;

      setConversation((prev) => {
        if (!prev) return prev;

        const next = prev.clone();
        const targetItem = next.thread.find((item) => item.id === itemId);

        if (!targetItem) return prev;
        const targetBlock = targetItem.contentBlocks?.find((block) => block.id === blockId);

        if (!targetBlock) return prev;

        targetBlock.adaptiveInputs = {
          ...(targetBlock.adaptiveInputs ?? {}),
          ...inputs,
        };

        return next;
      });
    },
    [setConversation],
  );

  const submitTaskApproval = useCallback(
    async (
      _itemId: string | undefined,
      block: CopilotConversationContentBlock,
      payload: AdaptiveSubmit<JsonObject>,
    ) => {
      let connection: IHubConnection | null = null;

      try {
        setIsLoading(true);
        dispatchStream({ type: 'reset' });
        connection = await getCopilotHubConnection();
        const connectionId = connection?.connectionId ?? null;
        if (connectionId) {
          setHubConnection(connection?.connection);
          setIsStreaming(true);
        }

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const answer = await apiCopilotSubmitTaskApproval(
          {
            sessionId: Number(payload.actionData.sessionId ?? copilotSessionId),
            approvalId: Number(payload.actionData.approvalId),
            adaptiveCardBlockId: String(payload.actionData.adaptiveCardBlockId ?? block.id ?? ''),
            approved: payload.actionData.decision === 'approve',
            adaptiveInputs: payload.inputs,
          },
          connectionId,
          accessToken,
        );
        if (answer) {
          setCopilotSessionId(answer.sessionId);
          setConversation((prev) => mergeAdaptiveInputs(prev, answer.conversation));

          // an approved mutation may have changed the entity the scene is showing (an approved
          // risk_update rewrote a field); the scene decides whether this concerns it. The active usage
          // names the entity first, so an approval under the control audit or the annual plan carries its own.
          const runtime = getRuntime();
          const entityRef = (activeDefinition ?? genericDefinition)?.getEntityRef(runtime.content);
          copilotPanelResultBus.publishGeneralCompletion(
            runtime.content?.context ?? CopilotPanelContext.Generic,
            entityRef?.entityType,
            entityRef?.entityId,
            true,
          );
        }
      } catch (err) {
        onErrorResponse(err);
      } finally {
        setStreamStatus(undefined);
        setIsLoading(false);
        setIsStreaming(false);
        if (!keepConnectionAlive) await stopHubConnection(connection?.connection);
      }
    },
    [
      activeDefinition,
      appContext,
      copilotSessionId,
      dispatchStream,
      genericDefinition,
      getCopilotHubConnection,
      getRuntime,
      keepConnectionAlive,
      onErrorResponse,
      setConversation,
      setCopilotSessionId,
      setHubConnection,
      setIsLoading,
      setIsStreaming,
      setStreamStatus,
    ],
  );

  const onAdaptiveSubmit = useCallback(
    async (itemId: string | undefined, block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => {
      if (payload.actionData.action !== 'aiTaskApproval') {
        // The question card's inputs are persisted only when a running process ACCEPTED the submit (phase 32, WB2):
        // a dead process must not leave a block whose record claims the answers were sent.
        const isQuestionsCard = payload.actionData.action === questionsCardAction;
        if (!isQuestionsCard) onAdaptiveInputsChanged(itemId, block.id, payload.inputs);

        // A usage may own this card (the framework audit's standard selection starts its process from
        // here). It appends its own conversation item; the answer round-trip stays with the panel.
        if (!conversation) return;
        // The field proposal card belongs to no usage: a text field binds its actions from any scene, so
        // the shell answers it before the active usage sees the card. Nothing to ask the server.
        if (handleFieldProposalSubmit(getRuntime(conversation, actions), block, payload, t)) return;

        const handled = await getProcessDefinition(getRunningProcessId(actions))?.onAdaptiveCardSubmit(
          getRuntime(conversation, actions),
          block,
          payload,
          t,
        );
        if (handled === true) {
          if (isQuestionsCard) onAdaptiveInputsChanged(itemId, block.id, payload.inputs);
          await getAnswer(conversation, actions);

          return;
        }

        // A question card no process took (the general knowledge loop asked it): the card keeps the sent
        // answers and their wording is the next turn, on the skill of the turn that asked.
        if (isQuestionSetCardSubmit(block, payload)) {
          const answerText = getQuestionSetAnswerText(payload.inputs);
          if (!answerText) return;

          const answered = withQuestionSetAnswers(conversation, itemId, block.id, payload.inputs);
          await submitQuestion(answered, answerText, undefined, lastSkillRef.current, getQuestionSetId(payload.inputs));
        }

        return;
      }

      const isApprove = payload.actionData.decision === 'approve';
      const blockPayload = block.payload as { hasDeleteOperations?: boolean } | undefined;
      if (isApprove && blockPayload?.hasDeleteOperations === true) {
        setPendingDeleteApproval({ itemId, block, payload });

        return;
      }

      await submitTaskApproval(itemId, block, payload);
    },
    [
      getProcessDefinition,
      actions,
      conversation,
      getAnswer,
      getRuntime,
      onAdaptiveInputsChanged,
      setPendingDeleteApproval,
      submitQuestion,
      submitTaskApproval,
      t,
    ],
  );

  const onApprovalDetails = useCallback(
    (_itemId: string | undefined, _block: CopilotConversationContentBlock, payload: AdaptiveSubmit<JsonObject>) => {
      const raw = payload.actionData.operations;
      const rawArr = Array.isArray(raw) ? (raw as unknown[]) : [];
      const ops: CopilotApprovalOperation[] = rawArr
        .filter((op): op is Record<string, unknown> => typeof op === 'object' && op !== null && !Array.isArray(op))
        .map((op) => ({
          toolName: typeof op['toolName'] === 'string' ? op['toolName'] : '',
          arguments: formatApprovalDetailsValue(op['arguments']),
          message: typeof op['message'] === 'string' ? op['message'] : '',
          entityCode: typeof op['entityCode'] === 'string' ? op['entityCode'] : '',
          entityName: typeof op['entityName'] === 'string' ? op['entityName'] : '',
          targetId: typeof op['targetId'] === 'string' ? op['targetId'] : '',
          outcome: getApprovalOperationOutcome(op['outcome']),
        }));
      setApprovalDetailsOperations(ops);
    },
    [setApprovalDetailsOperations],
  );

  return {
    getAnswer,
    onErrorResponse,
    onNewQuestion,
    onEditLastQuestion,
    onEntitySelection,
    onSceneEntitySelection,
    onDocumentAttached,
    onAdaptiveInputsChanged,
    submitTaskApproval,
    onAdaptiveSubmit,
    onApprovalDetails,
  };
};
